import React from "react";
import "@/App.css";
import "@/index.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { Toaster } from "sonner";
import { useTranslation } from "react-i18next";import { SessionProvider } from "./context/SessionContext";
import { AuthProvider } from "./context/AuthContext";
import { StealthProvider } from "./context/StealthContext";
import { DemoModeProvider } from "./context/DemoModeContext";
import { PublicDemoProvider } from "./context/PublicDemoContext";
import PublicDemoBadge from "./components/PublicDemoBadge";
import PublicDemoBootstrap from "./components/PublicDemoBootstrap";
import DemoModeBanner from "./components/DemoModeBanner";
import InstallPwaPrompt from "./components/InstallPwaPrompt";
import PrivacyConsent from "./components/PrivacyConsent";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";

import Welcome from "./pages/Welcome";
import Start from "./pages/Start";
import Login from "./pages/Login";
import MagicLinkVerify from "./pages/MagicLinkVerify";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Onboarding from "./pages/Onboarding";
import Capture from "./pages/Capture";
import Analyzing from "./pages/Analyzing";
import Routine from "./pages/Routine";
import MyRoutines from "./pages/MyRoutines";
import TechniqueGallery from "./pages/Techniques";
import TechniqueDetail from "./pages/TechniqueDetail";
import Pride from "./pages/Pride";
import PrideSuccess from "./pages/PrideSuccess";
import YCAccess from "./pages/YCAccess";
import YCClaim from "./pages/YCClaim";
import TutorialLibrary from "./pages/TutorialLibrary";
import Home from "./pages/Home";
import Pricing from "./pages/Pricing";
import BillingSuccess from "./pages/BillingSuccess";
import LockIn from "./pages/LockIn";
import LockInSuccess from "./pages/LockInSuccess";
import Recreate from "./pages/Recreate";
import About from "./pages/About";
import AffirmingResources from "./pages/AffirmingResources";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Contact from "./pages/Contact";
import Partners from "./pages/Partners";
import Enterprise from "./pages/Enterprise";
import Support from "./pages/Support";
import EducationDirectory from "./pages/EducationDirectory";
import EducationArticle, { NotFound } from "./pages/EducationArticle";
import TrustPage from "./pages/TrustPage";
import AuthorProfile from "./pages/AuthorProfile";
import Settings from "./pages/Settings";
import Arsenal from "./pages/Arsenal";
import SchematicGallery from "./pages/SchematicGallery";
import AdminFeedback from "./pages/AdminFeedback";
import AdminBroadcasts from "./pages/AdminBroadcasts";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminConversionFunnel from "./pages/AdminConversionFunnel";
import AdminUsers from "./pages/AdminUsers";
import AdminEngagement from "./pages/AdminEngagement";
import AdminCustomer from "./pages/AdminCustomer";
import AdminHome from "./pages/AdminHome";
import AdminRevenue from "./pages/AdminRevenue";
import AdminProducts from "./pages/AdminProducts";
import AdminProductSeeder from "./pages/AdminProductSeeder";
import AdminBrandDiscovery from "./pages/AdminBrandDiscovery";
import AdminBrandCatalogSync from "./pages/AdminBrandCatalogSync";
import AdminFounderDocs from "./pages/AdminFounderDocs";
import AdminWaitlist from "./pages/AdminWaitlist";
import AdminBetaAllowlist from "./pages/AdminBetaAllowlist";
import AdminGiftCodes from "./pages/AdminGiftCodes";
import AdminAmbassadors from "./pages/AdminAmbassadors";
import AmbassadorClaim from "./pages/AmbassadorClaim";
import ProClaim from "./pages/ProClaim";
import AmbassadorDashboard from "./pages/AmbassadorDashboard";
import AmbassadorEntry from "./pages/AmbassadorEntry";
import AmbassadorsPublic from "./pages/AmbassadorsPublic";
import AmbassadorsApply from "./pages/AmbassadorsApply";
import TrouveChat from "./pages/TrouveChat";
import MyTrouveChats from "./pages/MyTrouveChats";
import Pitch from "./pages/Pitch";
import SampleRoutine from "./pages/SampleRoutine";
import { DemoEntry, DemoLegacyResult } from "./pages/Demo";
import LiveCoachDemo from "./pages/LiveCoachDemo";
import BrowCoachDemo from "./pages/BrowCoachDemo";
import FeedbackWidget from "./components/FeedbackWidget";
import TosReacceptGate from "./components/TosReacceptGate";
import LimitBanner from "./components/LimitBanner";
import MobileTabBar from "./components/MobileTabBar";
import PrideClaimBanner from "./components/PrideClaimBanner";
import { useAuth } from "./context/AuthContext";
import { track, gaPageView } from "./lib/analytics";
import { trackFP } from "./lib/firstPartyAnalytics";
import { captureUtmFromUrl, captureLandingFingerprint } from "./lib/utm";
import { bootClarity } from "./lib/clarityLoader";
import { useStealth } from "./context/StealthContext";

function BillingCompletionGate({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || !user?.billing_required) return children;
  const allowed = (
    location.pathname === "/pricing" ||
    location.pathname.startsWith("/billing/") ||
    location.pathname.startsWith("/lock-in") ||
    location.pathname.startsWith("/pro/claim") ||
    location.pathname.startsWith("/yc/claim") ||
    location.pathname.startsWith("/ambassador/claim")
  );
  if (allowed) return children;
  return <Navigate to="/pricing?verify_email=1" replace />;
}

function AppRouter() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const { stealth } = useStealth();

  // Disable the browser's built-in scroll-restoration on history
  // pop (back/forward). Without this the browser will restore the
  // scroll position from the previous visit which, combined with
  // SPAs that mount new components, lands users in random places.
  // We want every route change — and every fresh landing — to start
  // at the top unless an explicit #hash anchor is present.
  React.useEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      try { window.history.scrollRestoration = "manual"; } catch (_) { /* noop */ }
    }
  }, []);

  // On every route change (including the initial mount) scroll the
  // window back to the top. The exception is hash anchors (e.g.
  // /#recreate-section) — those let the browser's native anchor
  // jump happen so deep links still work.
  React.useEffect(() => {
    if (location.hash) return;
    // Use the "auto" instant scroll on route changes so the user
    // doesn't see a jarring smooth-scroll from the previous page's
    // scroll position. Smooth scrolling within a single page is
    // still fine — that lives in component-level handlers.
    try { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); } catch (_) {
      window.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `location.hash` is deliberately read once per path change without re-firing on hash-only updates (which are anchor jumps the user wants preserved).
  }, [location.pathname]);

  // Keep <html lang> in sync with i18next at runtime. The build-time
  // SSR snapshots already bake the correct `lang` attribute, but
  // client-side language switches (Settings → Language) need to
  // reflect onto the document for accessibility tools + screen readers.
  React.useEffect(() => {
    const apply = () => {
      if (typeof document !== "undefined" && i18n.language) {
        document.documentElement.lang = i18n.language;
      }
    };
    apply();
    i18n.on("languageChanged", apply);
    return () => i18n.off("languageChanged", apply);
  }, [i18n]);

  // Boot Microsoft Clarity once per session, gated by Stealth Mode.
  // When CLARITY_PROJECT_ID is unset in backend/.env the loader is a
  // no-op — set it once and every NEW visitor session gets recorded.
  // Re-runs when stealth flips OFF (user opts back into trackers) so
  // a late opt-in still gets Clarity for the rest of the visit.
  React.useEffect(() => {
    if (stealth) return;
    bootClarity({ stealthOn: false }).catch(() => { /* silent */ });
  }, [stealth]);

  // Fire one `app_loaded` per session, regardless of which route the
  // user lands on. Useful for measuring ToS / pitch-page reach as well
  // as "/" reach. Capture UTM params from the URL FIRST so the very
  // first event carries the originating campaign attribution.
  React.useEffect(() => {
    captureUtmFromUrl();
    // Capture http_referrer + landing_page_url ONCE per tab so the
    // founder can answer "where did this user come from?" on the
    // /admin/users page. Idempotent — first landing wins.
    captureLandingFingerprint();
    track("app_loaded");
    // First-party `landing_page_view` — fires once per session for
    // the cold-traffic landing-funnel chart on /admin/analytics. We
    // emit this separately from `page_view` (which fires on EVERY
    // route change) so the funnel can count truly-fresh sessions.
    trackFP("landing_page_view");
  }, []);

  // GA4 page_view on every React Router location change (including
  // the initial mount). The inline GA4 bootstrap in index.html has
  // `send_page_view: false`, so this is the *only* place GA4 page_views
  // come from in the SPA — no double-counting. Stealth-mode safe:
  // gaPageView() short-circuits when stealth is on.
  //
  // First-party `page_view` ALSO fires here for the privacy-safe funnel
  // pipeline. Its client is a hard no-op in Stealth Mode and does not
  // create an analytics session identifier.
  React.useEffect(() => {
    gaPageView(location.pathname, location.search);
    trackFP("page_view");
  }, [location.pathname, location.search]);

  return (
    <>
      {/* Persistent limit banner — renders only for authed Free-tier
          users who've hit 0/N routines remaining. Suppressed on
          focused / full-screen flows (Phase 2) so it never competes
          with the primary action: capture/analyzing/routine + Trouvé
          chat + AR mirror + settings + checkout + admin. */}
      {!/^\/(admin|billing|auth|login|onboarding|capture|analyzing|routine|trouve|live-coach-demo|settings|pride|lock-in|yc)/.test(location.pathname) && (
        <LimitBanner />
      )}
      <Routes>
        {/* Custom Google OAuth landing — Google redirects here with
            ?code=&state=; AuthCallback exchanges it for our session.
            REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
        <Route path="/auth/google" element={<AuthCallback />} />
        <Route path="/" element={<Welcome />} />
        {/* Route parity: /start renders the same Trouvé conversation
            experience as /. Founder direction Feb 8 2026 — both
            entry points (homepage and TikTok-landing) must show the
            same chat/voice interface. The legacy Start.jsx YC-pitch
            landing is retained on disk for possible future reuse
            but no longer mounted. */}
        <Route path="/start" element={<Welcome />} />
        <Route path="/sample-routine" element={<SampleRoutine />} />
        {/* Production-parity demo. These are the real funnel pages with
            persistence disabled; SessionContext holds everything in memory. */}
        <Route path="/demo" element={<DemoEntry />} />
        <Route path="/demo/onboarding" element={<Onboarding ephemeral />} />
        <Route path="/demo/capture" element={<Capture ephemeral />} />
        <Route path="/demo/analyzing" element={<Analyzing ephemeral />} />
        <Route path="/demo/routine" element={<Routine ephemeral />} />
        <Route path="/demo/result/:token" element={<DemoLegacyResult />} />
        {/* P5 — Real-Time AR Coaching demo (4 stub steps, no auth, no
            routine generation; same LiveCoach component as production). */}
        <Route path="/live-coach-demo" element={<LiveCoachDemo />} />
        <Route path="/live-coach-demo/brows" element={<BrowCoachDemo />} />
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/arsenal"
        element={
          <ProtectedRoute>
            <Arsenal />
          </ProtectedRoute>
        }
      />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/magic-link" element={<MagicLinkVerify />} />
      <Route path="/auth/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/auth/verify-email" element={<VerifyEmail />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/capture"
        element={
          <ProtectedRoute>
            <Capture />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analyzing"
        element={
          <ProtectedRoute>
            <Analyzing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/routine/:id"
        element={
          <ProtectedRoute>
            <Routine />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-routines"
        element={
          <ProtectedRoute>
            <MyRoutines />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tutorials/library"
        element={<TutorialLibrary />}
      />
      <Route
        path="/techniques"
        element={
          <ProtectedRoute>
            <TechniqueGallery />
          </ProtectedRoute>
        }
      />
      <Route
        path="/techniques/:id"
        element={
          <ProtectedRoute>
            <TechniqueDetail />
          </ProtectedRoute>
        }
      />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/pitch" element={<Pitch />} />
      {/* ── Founding lock-in: Instagram-shareable $49.99/yr-forever
            link. Public page (signup happens here). After Stripe success,
            users land on /lock-in/success which polls /api/billing/status
            and shows the founding-member celebration. ── */}
      <Route path="/lock-in" element={<LockIn />} />
      <Route path="/lock-in/success" element={<LockInSuccess />} />
      {/* Pride 2026 t-shirt QR activation. Same lifetime $49.99/yr
          mechanic as /lock-in, but uncapped and Pride-themed. */}
      <Route path="/pride" element={<Pride />} />
      <Route path="/pride/success" element={<PrideSuccess />} />
      {/* YC partner / founder invite — public landing page that sends
          a magic link, which lands the user on /yc/claim after sign-in
          for instant 6-month Pro redemption. See pages/YCAccess.jsx
          for the design rationale. */}
      <Route path="/yc" element={<YCAccess />} />
      <Route path="/yc/claim" element={<YCClaim />} />
      {/* Recreate-a-look: paste a YouTube link or describe a tutorial.
          We extract a brief, then hand the user to /capture where the
          existing selfie → routine → AR mirror pipeline runs with the
          brief in router state. */}
      <Route
        path="/recreate"
        element={
          <ProtectedRoute>
            <Recreate />
          </ProtectedRoute>
        }
      />
      <Route path="/dev/schematics" element={<SchematicGallery />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/feedback"
        element={
          <ProtectedRoute>
            <AdminFeedback />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/broadcasts"
        element={
          <ProtectedRoute>
            <AdminBroadcasts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute>
            <AdminAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics/funnel"
        element={
          <ProtectedRoute>
            <AdminConversionFunnel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/engagement"
        element={
          <ProtectedRoute>
            <AdminEngagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers/:userId"
        element={
          <ProtectedRoute>
            <AdminCustomer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/revenue"
        element={
          <ProtectedRoute>
            <AdminRevenue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products"
        element={
          <ProtectedRoute>
            <AdminProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products/seed"
        element={
          <ProtectedRoute>
            <AdminProductSeeder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/brand-discovery"
        element={
          <ProtectedRoute>
            <AdminBrandDiscovery />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/brand-catalog-sync"
        element={
          <ProtectedRoute>
            <AdminBrandCatalogSync />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/founder-docs"
        element={
          <ProtectedRoute>
            <AdminFounderDocs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/waitlist"
        element={
          <ProtectedRoute>
            <AdminWaitlist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/members"
        element={
          <ProtectedRoute>
            <AdminBetaAllowlist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/gift-codes"
        element={
          <ProtectedRoute>
            <AdminGiftCodes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ambassadors"
        element={
          <ProtectedRoute>
            <AdminAmbassadors />
          </ProtectedRoute>
        }
      />
      {/* Ambassador program.
          /ambassador (singular, canonical) — smart entry: guests see
          the public marketing/apply page, signed-in users see their
          dashboard. This is the URL the founder shares on TikTok /
          IG / Linktree, so it MUST work for everyone without an
          auth wall.
          /ambassador/claim — invite-email landing (?token=…).
          /ambassadors (plural) — back-compat alias for the public
          marketing page; older share links keep working. */}
      <Route path="/ambassador/claim" element={<AmbassadorClaim />} />
      {/* /pro/claim — gifted-Pro one-tap landing (?token=…). The token
          is the credential; account + Pro + session in one step. */}
      <Route path="/pro/claim" element={<ProClaim />} />
      <Route path="/ambassador" element={<AmbassadorEntry />} />
      {/* /ambassadors (with the trailing s) is the PUBLIC marketing
          page + self-apply funnel. Anyone can land here from a
          TikTok / IG / Linktree link, scan the offer, and apply
          without an invite. The founder reviews each application
          at /admin/ambassadors (Applications tab) and approves with
          one click → that mints the invite + emails the claim
          link via Resend. */}
      <Route path="/ambassadors" element={<AmbassadorsPublic />} />
      <Route path="/ambassadors/apply" element={<AmbassadorsApply />} />
      {/* Interactive Trouvé — voice + vision Q&A chat. Gated server-side
          to Pro · ambassador · ambassador-referred (5/day quota). The page
          itself renders an upgrade panel for blocked tiers, so we
          intentionally do NOT wrap in ProtectedRoute — guests get the
          login redirect via the chat panel's own access check. */}
      <Route
        path="/trouve/chat"
        element={
          <ProtectedRoute>
            <TrouveChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-trouve-chats"
        element={
          <ProtectedRoute>
            <MyTrouveChats />
          </ProtectedRoute>
        }
      />
      {/* Backwards-compat: old /admin/beta links still resolve to the
          new Members page (same component, same data). */}
      <Route
        path="/admin/beta"
        element={
          <ProtectedRoute>
            <AdminBetaAllowlist />
          </ProtectedRoute>
        }
      />
      <Route path="/about" element={<About />} />
      <Route path="/resources" element={<AffirmingResources />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/enterprise" element={<Enterprise />} />
      <Route path="/support" element={<Support />} />
      <Route path="/lessons" element={<EducationDirectory contentType="lesson" />} />
      <Route path="/lessons/:slug" element={<EducationArticle contentType="lesson" />} />
      <Route path="/guides" element={<EducationDirectory contentType="guide" />} />
      <Route path="/guides/:slug" element={<EducationArticle contentType="guide" />} />
      <Route path="/editorial-standards" element={<TrustPage page="editorial" />} />
      <Route path="/recommendation-methodology" element={<TrustPage page="recommendations" />} />
      <Route path="/privacy/image-processing" element={<TrustPage page="imagePrivacy" />} />
      <Route path="/corrections" element={<TrustPage page="corrections" />} />
      <Route path="/affiliate-disclosure" element={<TrustPage page="affiliate" />} />
      <Route path="/authors/iyal-basen" element={<AuthorProfile />} />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/success"
        element={
          <ProtectedRoute>
            <BillingSuccess />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
      </Routes>
      <PrideClaimBanner />
      <MobileTabBar />
      {/* Feedback pill — hidden on transactional / auth / admin /
          campaign and enterprise landing routes so it doesn't compete with the
          primary action. Phase 4 (Feb 27 2026): logged-in users in
          checkout/billing or guests in the auth funnel should never
          see a floating ask. */}
      {!/^\/(start|login|auth|billing|admin|enterprise|lock-in|pricing|pride\/success|yc)/.test(location.pathname) && <FeedbackWidget />}
      <TosReacceptGate />
    </>
  );
}

function App() {
  // Locale-prefixed URLs (`/fr/...`, `/es/...`) are first-class routes:
  // build-time SSR snapshots live at those paths so crawlers can index
  // localized landing copy. We detect the prefix here and feed it to
  // BrowserRouter as a basename, which lets the existing <Route path="/">
  // / <Route path="/about"> definitions match without duplication.
  const localeBasename = React.useMemo(() => {
    if (typeof window === "undefined") return "/";
    const m = (window.location.pathname || "/").match(/^\/(fr|es|pt)(\/|$)/);
    return m ? `/${m[1]}` : "/";
  }, []);

  return (
    <div className="App">
      <BrowserRouter basename={localeBasename}>
        <AuthProvider>
          <PublicDemoProvider>
            <StealthProvider>
              <DemoModeProvider>
                <SessionProvider>
                  <PublicDemoBootstrap />
                  <PublicDemoBadge />
                  <DemoModeBanner />
                  <BillingCompletionGate>
                    <AppRouter />
                  </BillingCompletionGate>
                  <PrivacyConsent />
                  {/* Iter 83 — global "Add to Home Screen" prompt.
                      Lives at App-root so it can appear on any route
                      after the engagement delay (see InstallPwaPrompt
                      for dismissal + standalone-detection logic).
                      Mobile-only (`lg:hidden` in the component). */}
                  <InstallPwaPrompt />
                <Toaster
                  position="top-center"
                  richColors
                  theme="light"
                  toastOptions={{
                    style: {
                      borderRadius: "18px",
                      fontFamily: "'Work Sans', sans-serif",
                    },
                  }}
                />
              </SessionProvider>
              </DemoModeProvider>
            </StealthProvider>
          </PublicDemoProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
